import type { UserProfile } from '@/store/usersApi';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Generate and download a User Profile PDF laid out like the provided mock.
 * Uses an offscreen DOM node + html2canvas to match existing report export approach.
 */
export async function exportUserProfilePdf(
  profile: UserProfile,
  t: (key: string) => string,
  fileName?: string,
): Promise<void> {
  // Offscreen container
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.top = '-10000px';
  container.style.left = '-10000px';
  container.style.width = '800px'; // fixed width for consistent layout
  container.style.background = '#ffffff';
  container.style.padding = '24px';
  container.style.fontFamily = 'Arial, Helvetica, sans-serif';

  const valueOrNA = (v?: string | null) => (v ? v : '—');

  // Build simple table-style layout using semantic markup
  container.innerHTML = `
    <div style="display:flex; flex-direction:column; gap:16px;">
      ${row(t('userProfilePdf.firstName'), profile.FirstName)}
      ${row(t('userProfilePdf.lastName'), valueOrNA(profile.LastName))}
      ${row(t('userProfilePdf.email'), profile.EmailAddress)}
      ${row(t('userProfilePdf.phone'), valueOrNA(profile.PhoneNumber))}
      ${row(t('userProfilePdf.addressLine1'), valueOrNA(profile.AddressLine1))}
      ${row(t('userProfilePdf.addressLine2'), valueOrNA(profile.Address))}
      ${row(t('userProfilePdf.city'), valueOrNA(profile.City))}
      ${row(t('userProfilePdf.state'), valueOrNA(profile.State))}
      ${row(t('userProfilePdf.zip'), valueOrNA(profile.Zip))}
    </div>
  `;

  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
    });

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'a4',
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 40;
    const usableWidth = pageWidth - margin * 2;
    const usableHeight = pageHeight - margin * 2;

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const widthScale = usableWidth / imgWidth;
    const heightScale = usableHeight / imgHeight;
    const scale = Math.min(widthScale, heightScale);

    const renderWidth = imgWidth * scale;
    const renderHeight = imgHeight * scale;

    const imgData = canvas.toDataURL('image/png', 1.0);
    pdf.addImage(imgData, 'PNG', margin, margin, renderWidth, renderHeight);
    pdf.save(`${fileName ?? `${profile.FirstName}_${profile.LastName}`}.pdf`);
  } finally {
    // Cleanup offscreen DOM
    container.remove();
  }
}

function row(label: string, value: string): string {
  return `
    <div style="display:flex; gap:24px; align-items:baseline;">
      <div style="min-width:220px; font-weight:700; font-size:22px;">${escapeHtml(
        label,
      )}</div>
      <div style="font-size:22px;">${escapeHtml(value)}</div>
    </div>
  `;
}

function escapeHtml(text: string): string {
  const span = document.createElement('span');
  span.textContent = text;
  return span.innerHTML;
}
